// app/shared/components/button/HTFButtonComponent.tsx



// !v1 correct size shadow problem
// ────────────────────────────────────────────────────────────────
// app/shared/components/ui/HTFStyledButton.tsx
// PostHog-style button with properly sized 3D effect
// ────────────────────────────────────────────────────────────────
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { ButtonHTMLAttributes, forwardRef } from 'react'

import { cn } from '@/lib/utils'

/* ░░ WRAPPER - Container for 3D effect ░░ */
const wrapperVariants = cva(
  'relative inline-block',
  {
    variants: {
      variant: {
        primary:             '',
        'primary-outline':   '',
        secondary:           '',
        'secondary-outline': '',
        success:             '',
        warning:             '',
        destructive:         '',
        ghost:               '',
        link:                '',
      },
      size: {
        sm:      '',
        default: '',
        lg:      '',
        xl:      '',
        icon:    '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
)

/* ░░ BUTTON FACE - The actual clickable surface ░░ */
const buttonVariants = cva(
  'relative inline-flex items-center justify-center font-bold rounded-lg ' +
    'select-none transition-transform duration-100 ease-out whitespace-nowrap ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        /* 3D variants with thicker bottom borders */
        primary:             'bg-yellow text-black border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-yellow-dark',
        'primary-outline':   'bg-white text-black border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-yellow-dark',
        secondary:           'bg-teal text-white border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-teal-dark',
        'secondary-outline': 'bg-white text-black border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-teal-dark',
        success:             'bg-green text-white border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-green-dark',
        warning:             'bg-yellow text-black border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-yellow-dark',
        destructive:         'bg-orange text-white border-t-[1.5px] border-l-[1.5px] border-r-[1.5px] border-b-[6px] border-orange-dark',

        /* Flat variants */
        ghost: 'bg-transparent text-gray-700 border-transparent hover:bg-gray-100',
        link:  'bg-transparent border-transparent text-primary hover:underline p-0',
      },
      size: {
        sm:      'px-3 py-1.5 text-sm h-9 max-h-9',
        default: 'px-5 py-2   text-[15px] h-10 max-h-10',
        lg:      'px-6 py-3   text-base h-12 max-h-12',
        xl:      'px-8 py-4   text-lg h-14 max-h-14',
        icon:    'p-2.5 w-10 h-10 max-w-10 max-h-10 text-[15px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
)

/* ░░ 3D EFFECT STYLES ░░ */
// Using ::before pseudo-element for the 3D ledge
const threeDEffectStyles = {
  // Base 3D setup
  base: 'before:absolute before:inset-x-0 before:top-full before:border-[4px]',
  
  // Size-specific heights
  size: {
    sm:      'before:h-[4px] mb-[4px]',
    default: 'before:h-[5px] mb-[5px]',
    lg:      'before:h-[6px] mb-[6px]',
    xl:      'before:h-[7px] mb-[7px]',
    icon:    'before:h-[5px] mb-[5px]',
  },
  
  // Variant-specific colors
  variant: {
    primary:             'before:bg-yellow-dark before:border-yellow-dark',
    'primary-outline':   'before:bg-yellow-dark before:border-yellow-dark',
    secondary:           'before:bg-teal-dark before:border-teal-dark',
    'secondary-outline': 'before:bg-teal-dark before:border-teal-dark',
    success:             'before:bg-green-dark before:border-green-dark',
    warning:             'before:bg-yellow-dark before:border-yellow-dark',
    destructive:         'before:bg-orange-dark before:border-orange-dark',
  },
  
  // Transform states
  transform: {
    sm:      'translate-y-0 hover:-translate-y-0.5 active:translate-y-[3px] disabled:!translate-y-0',
    default: 'translate-y-0 hover:-translate-y-0.5 active:translate-y-[4px] disabled:!translate-y-0',
    lg:      'translate-y-0 hover:-translate-y-0.5 active:translate-y-[5px] disabled:!translate-y-0',
    xl:      'translate-y-0 hover:-translate-y-0.5 active:translate-y-[6px] disabled:!translate-y-0',
    icon:    'translate-y-0 hover:-translate-y-0.5 active:translate-y-[4px] disabled:!translate-y-0',
  },
}

/* ░░ COMPONENT PROPS ░░ */
export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  icon?: React.ReactNode
  iconAfter?: React.ReactNode
}

/* ░░ 3D VARIANTS SET ░░ */
const _3D = new Set([
  'primary',
  'primary-outline',
  'secondary',
  'secondary-outline',
  'success',
  'warning',
  'destructive',
])

/* ░░ COMPONENT ░░ */
export const HTFButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'default',
      asChild = false,
      isLoading = false,
      icon,
      iconAfter,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const is3D = _3D.has(variant as string)
    const isDisabled = isLoading || disabled

    /* Flat link variant - no wrapper needed */
    if (variant === 'link') {
      return (
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size }), className)}
          disabled={isDisabled}
          {...props}
        >
          {isLoading ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            icon && <span className="mr-1.5 shrink-0">{icon}</span>
          )}
          {children}
          {!isLoading && iconAfter && (
            <span className="ml-1.5 shrink-0">{iconAfter}</span>
          )}
        </Comp>
      )
    }

    /* 3D variants with PostHog-style bottom border */
    if (is3D) {
      return (
        <Comp
          ref={ref}
          className={cn(
            buttonVariants({ variant, size }),
            'translate-y-0 hover:-translate-y-0.5 active:translate-y-[2px] disabled:!translate-y-0',
            className
          )}
          disabled={isDisabled}
          {...props}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            icon && <span className="mr-2 shrink-0">{icon}</span>
          )}
          {children}
          {!isLoading && iconAfter && (
            <span className="ml-2 shrink-0">{iconAfter}</span>
          )}
        </Comp>
      )
    }

    /* Flat ghost variant */
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          icon && <span className="mr-2 shrink-0">{icon}</span>
        )}
        {children}
        {!isLoading && iconAfter && (
          <span className="ml-2 shrink-0">{iconAfter}</span>
        )}
      </Comp>
    )
  },
)
HTFButton.displayName = 'HTFButton'



